export interface RoutePrivilegeNode {
  id: string;
  hasAccess?: boolean;
  path?: string | null;
  children?: RoutePrivilegeNode[];
}

/** Always-allowed escape hatch so users are never fully locked out. */
export const ALWAYS_ALLOWED_PATHS = ['/portal/my-account'] as const;

/**
 * Real router path prefixes → privilege catalog ids (Sidebar / mockRoutes).
 * Longest-prefix match wins — keep more specific paths before checking shorter ones via sort.
 */
export const PORTAL_ROUTE_PRIVILEGES: ReadonlyArray<{ pathPrefix: string; id: string }> = [
  { pathPrefix: '/portal/home', id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890' },
  { pathPrefix: '/portal/workflows', id: 'b2c3d4e5-f6g7-8901-bcde-f12345678901' },
  { pathPrefix: '/portal/orchestration', id: 'b2c3d4e5-f6g7-8901-bcde-f12345678901' },
  { pathPrefix: '/portal/flowbuilder', id: 'b2c3d4e5-f6g7-8901-bcde-f12345678901' },
  { pathPrefix: '/portal/applications', id: '6f3a0f4c-20b7-4c2a-b4c1-7caaed2358b7' },
  { pathPrefix: '/portal/chatagent', id: '4e72e1aa-8c6d-4d2b-bd35-0df9a5df4d01' },
  { pathPrefix: '/portal/createchatagent', id: '4e72e1aa-8c6d-4d2b-bd35-0df9a5df4d01' },
  { pathPrefix: '/portal/chatbot', id: '4e72e1aa-8c6d-4d2b-bd35-0df9a5df4d01' },
  { pathPrefix: '/portal/agents', id: 'd918c2ef-0c74-46c7-b50d-dcf4d0d7c59d' },
  { pathPrefix: '/portal/agentStudio', id: 'd918c2ef-0c74-46c7-b50d-dcf4d0d7c59d' },
  { pathPrefix: '/portal/knowledge-base', id: 'h8i9j0k1-l2m3-4567-2345-678901234567' },
  { pathPrefix: '/portal/analytics-hub', id: '70b4dff6-7d13-43b0-a6d6-cba6b5b4a8ce' },
  { pathPrefix: '/portal/configurations/data-ingestion', id: 'f6g7h8i9-j0k1-2345-f123-456789012345' },
  { pathPrefix: '/portal/configurations/llm-routing', id: 'k1l2m3n4-o5p6-7890-5678-901234567890' },
  { pathPrefix: '/portal/configurations/data-vault', id: 'o5p6q7r8-s9t0-1234-9012-345678901234' },
  { pathPrefix: '/portal/configurations/schedules', id: 's9t0u1v2-w3x4-5678-3456-789012345678' },
  { pathPrefix: '/portal/mcpdashboard', id: 'a1d2f3b4-5c6e-47a8-9b01-2c3d4e5f6a7b' },
  { pathPrefix: '/portal/mcpdevelopers', id: 'b2e3c4d5-6f7a-48b9-0c12-3d4e5f6a7b8c' },
  { pathPrefix: '/portal/mcpresources-v2', id: 'c3f4d5e6-7a8b-49c0-1d23-4e5f6a7b8c9d' },
  { pathPrefix: '/portal/mcpresources', id: 'c3f4d5e6-7a8b-49c0-1d23-4e5f6a7b8c9d' },
  { pathPrefix: '/portal/mcplivemonitor', id: 'd4a5e6f7-8b9c-40d1-2e34-5f6a7b8c9d0e' },
  { pathPrefix: '/portal/ai-governance', id: 'g1h2i3j4-k5l6-7890-abcd-ef12345678901' },
  { pathPrefix: '/portal/connections', id: 'e1f2g3h4-i5j6-7890-5678-901234567890' },
  { pathPrefix: '/portal/access-settings', id: 'h4i5j6k7-l8m9-0123-8901-234567890123' },
  { pathPrefix: '/portal/system-settings', id: 'm9n0o1p2-q3r4-5678-3456-789012345678' },
  { pathPrefix: '/portal/my-account', id: 'n0o1p2q3-r4s5-6789-4567-890123456789' },
  { pathPrefix: '/portal/logs', id: 'o1p2q3r4-s5t6-7890-5678-901234567890' },
  { pathPrefix: '/portal/deployments', id: 'a7b8c9d0-e1f2-3456-1234-567890123456' },
  { pathPrefix: '/portal/intent-orchestration', id: 'w3x4y5z6-a7b8-9012-7890-123456789012' },
  { pathPrefix: '/portal/reports', id: 'p2q3r4s5-t6u7-8901-6789-012345678901' },
  { pathPrefix: '/chat', id: 'q3r4s5t6-u7v8-9012-7890-123456789012' },
];

const SORTED_PRIVILEGES = [...PORTAL_ROUTE_PRIVILEGES].sort(
  (a, b) => b.pathPrefix.length - a.pathPrefix.length
);

export function normalizePortalPath(path: string): string {
  if (!path) return path;
  if (path.startsWith('/portal') || path.startsWith('/chat')) return path;
  return `/portal/${path.replace(/^\/+/, '')}`;
}

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function resolvePrivilegeIdForPath(pathname: string): string | null {
  const normalized = pathname.startsWith('/portal') || pathname.startsWith('/chat')
    ? pathname
    : normalizePortalPath(pathname);

  for (const entry of SORTED_PRIVILEGES) {
    if (pathMatchesPrefix(normalized, entry.pathPrefix)) {
      return entry.id;
    }
  }
  return null;
}

function findAccessFlag(
  id: string,
  routes: RoutePrivilegeNode[]
): boolean | undefined {
  for (const route of routes) {
    if (route.id === id) return route.hasAccess ?? false;
    if (route.children?.length) {
      const nested = findAccessFlag(id, route.children);
      if (nested !== undefined) return nested;
    }
  }
  return undefined;
}

/** Missing privilege id ⇒ denied. */
export function checkPrivilegeAccess(
  id: string,
  routes: RoutePrivilegeNode[]
): boolean {
  return findAccessFlag(id, routes) ?? false;
}

/**
 * Returns whether the current pathname is allowed given privilege routes.
 * - Always-allowed paths (my-account) → true
 * - Mapped path with hasAccess → that flag
 * - Mapped path missing from routes / hasAccess false → false
 * - Unmapped path → true (auth-only; not in privilege catalog)
 */
export function canAccessPath(
  pathname: string,
  routes: RoutePrivilegeNode[]
): boolean {
  if (ALWAYS_ALLOWED_PATHS.some((p) => pathMatchesPrefix(pathname, p))) {
    return true;
  }

  const privilegeId = resolvePrivilegeIdForPath(pathname);
  if (!privilegeId) return true;

  return checkPrivilegeAccess(privilegeId, routes);
}

export function getUserRoutesFromStorage(): RoutePrivilegeNode[] {
  try {
    const parsed = JSON.parse(localStorage.getItem('user_routes') || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
